import { Bell, Bookmark, Megaphone, Menu as MenuIcon, Search, Users } from '@/components/ui/icons';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CountBadge } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/Button';
import { SavedPanel } from '@/components/saved/SavedPanel';
import { NotificationInbox } from '@/components/social/NotificationInbox';
import { useIsDesktop, useIsWide } from '@/hooks/useAppEffects';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/stores/social';
import { useUi } from '@/stores/ui';

interface TopBarProps {
  icon?: ReactNode;
  /** Set false to drop a purely decorative icon on phones, where the header is tight. */
  iconOnPhones?: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  /** A second line under the title on phones (e.g. "Emberwood · 38 online"). */
  mobileSubtitle?: ReactNode;
  children?: ReactNode;
  showMembersToggle?: boolean;
  showSearch?: boolean;
  /** What the search pill says, e.g. "Search Emberwood". */
  searchLabel?: string;
}

export function TopBar({ icon, iconOnPhones = true, title, subtitle, mobileSubtitle, children, showMembersToggle, showSearch = true, searchLabel = 'Search' }: TopBarProps) {
  const isDesktop = useIsDesktop();
  const isWide = useIsWide();
  const setNavOpen = useUi((s) => s.setNavOpen);
  const membersOpen = useUi((s) => s.membersOpen);
  const setMembersOpen = useUi((s) => s.setMembersOpen);
  const searchOpen = useUi((s) => s.searchOpen);
  const setSearchOpen = useUi((s) => s.setSearchOpen);
  const unread = useNotifications((s) => s.unreadCount);
  const [inboxOpen, setInboxOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const savedRef = useRef<HTMLButtonElement>(null);

  return (
    <header data-cf="top-bar" className="flex h-16 shrink-0 items-center gap-2.5 bg-surface px-3 sm:gap-3 sm:px-6">
      {!isDesktop && (
        <IconButton label="Open navigation" onClick={() => setNavOpen(true)} className="h-10! w-10! bg-sunken text-fg!">
          <MenuIcon size={20} />
        </IconButton>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {icon && <span className={cn('shrink-0 text-accent', !iconOnPhones && 'hidden sm:inline')}>{icon}</span>}
        <div className="min-w-0 leading-tight">
          <h1 className="truncate text-lg text-fg sm:text-xl">{title}</h1>
          {mobileSubtitle && !isDesktop && <div className="truncate text-xs text-fg-muted">{mobileSubtitle}</div>}
        </div>
        {subtitle && <div className="hidden min-w-0 flex-1 truncate text-[13px] text-fg-muted sm:block">{subtitle}</div>}
      </div>
      <div className="-mr-1 flex shrink-0 items-center sm:mr-0 sm:gap-1">
        {children}
        {showSearch &&
          (isWide ? (
            <button
              type="button"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-pressed={searchOpen}
              className={cn(
                'mr-1 flex h-9 w-52 items-center gap-2 rounded-full px-3.5 text-left text-[13px] text-fg-muted transition-colors',
                searchOpen ? 'bg-active text-fg' : 'bg-sunken hover:text-fg-soft',
              )}
            >
              <Search size={16} className="shrink-0" />
              <span className="truncate">{searchLabel}</span>
            </button>
          ) : (
            <IconButton label="Search messages" active={searchOpen} onClick={() => setSearchOpen(!searchOpen)}>
              <Search size={20} />
            </IconButton>
          ))}
        <IconButton label="Send feedback" onClick={() => useUi.getState().openModal({ type: 'feedback' })} className="hidden sm:inline-flex">
          <Megaphone size={20} />
        </IconButton>
        <IconButton ref={savedRef} label="Saved messages" active={savedOpen} onClick={() => setSavedOpen((v) => !v)}>
          <Bookmark size={20} />
        </IconButton>
        <SavedPanel open={savedOpen} onClose={() => setSavedOpen(false)} anchor={savedRef} />
        <span className="relative">
          <IconButton ref={bellRef} label="Notifications" active={inboxOpen} onClick={() => setInboxOpen((v) => !v)}>
            <Bell size={20} />
          </IconButton>
          <CountBadge count={unread} className="pointer-events-none absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px]" />
        </span>
        <NotificationInbox open={inboxOpen} onClose={() => setInboxOpen(false)} anchor={bellRef} />
        {showMembersToggle && (
          <IconButton
            label={membersOpen && !searchOpen ? 'Hide member list' : 'Show member list'}
            active={membersOpen && !searchOpen}
            onClick={() => {
              if (searchOpen) {
                setSearchOpen(false);
                setMembersOpen(true);
              } else setMembersOpen(!membersOpen);
            }}
          >
            <Users size={20} />
          </IconButton>
        )}
      </div>
    </header>
  );
}

/**
 * The right-hand column (member list / search). A column on wide screens and a
 * slide-over panel on smaller ones.
 */
export function RightPanel({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  const wide = useIsWide();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // As an overlay, Escape dismisses it (unless a dialog or popover handles the key first).
  useEffect(() => {
    if (wide) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('dialog[open]')) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wide]);

  if (wide) return <aside data-cf="right-panel" className="flex w-[260px] shrink-0 flex-col bg-panel">{children}</aside>;
  return (
    <>
      <div className="fixed inset-0 z-30 animate-fade-in bg-black/40" onClick={onClose} aria-hidden />
      <aside data-cf="right-panel" className={cn('fixed inset-y-0 right-0 z-40 flex w-[min(320px,88vw)] animate-slide-up flex-col bg-panel shadow-pop')}>
        {children}
      </aside>
    </>
  );
}
